import { REVIEWABLE_TASK_TYPES, type TaskStatus, type TaskType } from '@pms/shared';

interface StatusMeta {
  label: string;
  /** Tailwind classes for a pill badge (light + dark). */
  className: string;
  /** Solid hex (matches the reference Task Status config) — used for dots + swatches. */
  hex: string;
  description: string;
}

export const STATUS_META: Record<TaskStatus, StatusMeta> = {
  pending_accept:    { label: 'Pending',          className: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400', hex: '#F59E0B', description: 'Task awaiting acceptance by assignee' },
  in_progress:       { label: 'In Progress',      className: 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-400', hex: '#3B82F6', description: 'Task is being worked on' },
  for_review:        { label: 'In Review',        className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400', hex: '#6366F1', description: 'Submitted by the assignee, waiting on the task creator to approve it' },
  needs_improvement: { label: 'Need Improvement', className: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400', hex: '#F43F5E', description: 'Returned by the reviewer — the assignee updates it and submits again' },
  completed:         { label: 'Completed',        className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400', hex: '#10B981', description: 'Approved by the reviewer' },
  discarded:         { label: 'Discarded',        className: 'bg-slate-200 text-slate-600 dark:bg-slate-500/15 dark:text-slate-400', hex: '#6B7280', description: 'Closed without completion — set by the task creator, or by the daily reset when the deadline passed' },
};

/** Every status, in workflow order. Used for filter dropdowns and the config legend. */
export const ALL_STATUSES: TaskStatus[] = [
  'pending_accept', 'in_progress', 'for_review', 'needs_improvement', 'completed', 'discarded',
];

/**
 * The single action an ASSIGNEE can take from their current status, or null when the
 * ball is not in their court.
 *
 * `for_review` is absent on purpose: once submitted, the assignee waits. Complete always
 * posts `for_review` — the server downgrades that to `completed` for daily tasks, which
 * have no review step, so the client never has to know which types are reviewable.
 */
export const ASSIGNEE_ACTION: Partial<Record<TaskStatus, { label: string; next: TaskStatus }>> = {
  pending_accept: { label: 'Accept', next: 'in_progress' },
  in_progress: { label: 'Complete', next: 'for_review' },
  needs_improvement: { label: 'Complete', next: 'for_review' },
};

interface TypeMeta {
  label: string;
  /** Short badge abbreviation used on employee cards (D:7, M:1, …). */
  abbr: string;
  className: string;
}

export const TYPE_META: Record<TaskType, TypeMeta> = {
  daily:    { label: 'Daily',    abbr: 'D',  className: 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-400' },
  weekly:   { label: 'Weekly',   abbr: 'W',  className: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400' },
  monthly:  { label: 'Monthly',  abbr: 'M',  className: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400' },
  yearly:   { label: 'Yearly',   abbr: 'Y',  className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400' },
  // Shown as "One Time" on purpose — the stored value stays `periodic` (renaming it
  // would mean migrating every row for no user-visible gain). A periodic task is a
  // single occurrence created on its start date and due on its end date, which is how
  // one-time work is created here. Don't "fix" the mismatch.
  periodic: { label: 'One Time', abbr: 'OT', className: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400' },
};

export const ALL_TYPES: TaskType[] = ['daily', 'weekly', 'monthly', 'yearly', 'periodic'];

/**
 * Types whose Complete goes through a reviewer. Narrowed from the shared constant so
 * the review-queue filter can't offer `daily`, which never reaches that queue.
 */
export const REVIEWABLE_TYPES: TaskType[] = [...REVIEWABLE_TASK_TYPES];

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function fullName(first?: string | null, last?: string | null): string {
  return [first, last].filter(Boolean).join(' ') || '—';
}

export function initials(first?: string | null, last?: string | null): string {
  return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?';
}

/**
 * "9:00 AM" from an HH:MM or HH:MM:SS string — Postgres TIME columns carry the
 * seconds, so both shapes have to parse.
 */
export function formatTime12(t?: string | null): string {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return '—';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

/** "9:00 AM → 7:00 PM" style time-slot label from two HH:MM(:SS) strings. */
export function timeSlot(start?: string | null, end?: string | null): string | null {
  if (!start && !end) return null;
  return `${formatTime12(start)} → ${formatTime12(end)}`;
}

/** "1:07" style clock for a voice-note length in milliseconds. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
